// src/App.js
import React, { useContext, Suspense, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthContext } from "./components/AuthContext";

import Header from "./components/Header";
import Footer from "./components/Footer";

// === Strażnicy — importy z src/routes/ ===
import RequireAuth from "./routes/RequireAuth";
import TutoringProspectRoute from "./routes/TutoringProspectRoute";
import RequireAdmin from "./routes/RequireAdmin";
import RequireRole from "./routes/RequireRole";
import RequireAnyRole from "./routes/RequireAnyRole";
import RedirectIfAuth from "./routes/RedirectIfAuth";
import RequireCourseAccess from "./routes/RequireCourseAccess";
import RequireTutoringToolsAccess from "./routes/RequireTutoringToolsAccess";
import RequireInvestmentAccess from "./routes/RequireInvestmentAccess";

// ⬇️ starter synchronizacji postępu
import ProgressSyncInit from "./progress/ProgressSyncInit";
import GlobalFeedbackButton from "./components/GlobalFeedbackButton";
import GlobalNavigationFeedback from "./components/GlobalNavigationFeedback";
import SeoGuestLearningGate from "./components/SeoGuestLearningGate";
import PokerClockPage from "./poker/PokerClockPage";
import PokerLobbyPage from "./poker/pages/PokerLobbyPage";
import PokerTablePage from "./poker/pages/PokerTablePage";
import FifaPage from "./fifa/FifaPage";
import FifaInvitePage from "./fifa/FifaInvitePage";
import WhiteboardLobbyPage from "./whiteboard/pages/WhiteboardLobbyPage";
import WhiteboardRoomPage from "./whiteboard/pages/WhiteboardRoomPage";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { legacyMaturaSeoRedirects, seoTopicRegistry } from "./seoTopicRegistry";
import LandingPage from "./pages/LandingPage";
import GrantInvestorTourPage from "./pages/GrantInvestorTourPage";
import {
  trackGoogleConversion,
  trackMarketingEvent,
  trackMarketingPageView,
} from "./utils/marketingMeasurement";

const TutoringLandingPage = React.lazy(() => import("./pages/TutoringLandingPage"));

const ADS_CLICK_SESSION_KEY = "mt_ads_click_session_v1";
const ADS_CONVERSION_SESSION_KEY = "mt_ads_view_conversion_sent_v1";

function hasAdsClickParam(search) {
  const q = new URLSearchParams(search || "");
  return Boolean(q.get("gclid") || q.get("gbraid") || q.get("wbraid") || q.get("msclkid"));
}

function isQualifiedViewPath(pathname) {
  const path = String(pathname || "");
  if (path === "/home") return true;
  if (path === "/demo/matura/sciezka" || path === "/demo/arkusz-maturalny" || path.startsWith("/demo/matura-exam/")) return true;
  if (/^\/lekcja\/\d+\/\d+/.test(path)) return true;
  if (/^\/demo-temat\/lekcja\/\d+\/\d+/.test(path)) return true;
  return seoTopicRegistry.some((topic) => `/${topic.slug}` === path);
}


// === Lazy-loaded layouty i strony ===
const HomeLayout = React.lazy(() => import("./layouts/HomeLayout"));
const SectionLayout = React.lazy(() => import("./layouts/SectionLayout"));
const LessonLayout = React.lazy(() => import("./layouts/LessonLayout"));
const LessonEntryLayout = React.lazy(() => import("./layouts/LessonEntryLayout"));

const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const VerifyEmailPage = React.lazy(() => import("./pages/VerifyEmailPage"));
const CampaignActivationPage = React.lazy(() => import("./pages/CampaignActivationPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage")); // ✅ DODANE

const ParentDashboard = React.lazy(() => import("./pages/ParentDashboard"));
const TutorDashboard = React.lazy(() => import("./pages/TutorDashboard"));
const StudentDashboard = React.lazy(() => import("./pages/StudentDashboard"));
const StudentTutoringDashboard = React.lazy(() => import("./pages/StudentTutoringDashboard"));
const ParentChildTutoringDashboard = React.lazy(() => import("./pages/ParentChildTutoringDashboard"));
const RoleSelectionPage = React.lazy(() => import("./pages/RoleSelectionPage"));
const DebugTools = React.lazy(() => import("./pages/DebugTools"));
const DynamicQuizLoader = React.lazy(() => import("./pages/DynamicQuizLoader"));
const SeoTopicLandingPage = React.lazy(() =>
  import("./pages/SeoTopicLandingPage")
);
const GraphPage = React.lazy(() => import("./GraphPage"));
const HomeworkPage = React.lazy(() => import("./pages/HomeworkPage"));
const HomeworkLessonPage = React.lazy(() => import("./pages/HomeworkLessonPage"));
const MaturaExamPage = React.lazy(() => import("./pages/MaturaExamPage"));
const NumberDefensePage = React.lazy(() => import("./pages/NumberDefensePage"));

const QuizSnapshotView = React.lazy(() => import("./pages/QuizSnapshotView"));
const MaturaExamSnapshotView = React.lazy(() =>
  import("./pages/MaturaExamSnapshotView")
);
const UserAccount = React.lazy(() => import("./pages/UserAccount"));
const Platnosc = React.lazy(() => import("./pages/Platnosc"));

const Cennik = React.lazy(() => import("./pages/Cennik"));
const Regulamin = React.lazy(() => import("./pages/Regulamin"));
const PolitykaPrywatnosci = React.lazy(() =>
  import("./pages/PolitykaPrywatnosci")
);

const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const InvestmentDashboard = React.lazy(() => import("./pages/InvestmentDashboard"));
const StudentProgressView = React.lazy(() =>
  import("./pages/admin/StudentProgressView")
);

const PaymentOkPage = React.lazy(() => import("./pages/PaymentOkPage"));
const PaymentFailPage = React.lazy(() => import("./pages/PaymentFailPage"));

const SearchLessonsPage = React.lazy(() =>
  import("./pages/SearchLessonsPage")
);
const TextbookOutlinePage = React.lazy(() =>
  import("./pages/TextbookOutlinePage")
);
const MaturaStudentPanel = React.lazy(() =>
  import("./pages/MaturaStudentPanel")
);
const MaturaCoursePathPage = React.lazy(() =>
  import("./pages/MaturaCoursePathPage")
);
const MaturaPlannerPage = React.lazy(() =>
  import("./pages/MaturaPlannerPage")
);
const MaturaCourseTasksPage = React.lazy(() =>
  import("./pages/MaturaCourseTasksPage")
);
const MaturaCoursePreparationPage = React.lazy(() =>
  import("./pages/MaturaCoursePreparationPage")
);
const MaturaPapersPage = React.lazy(() => import("./pages/MaturaPapersPage"));
const MaturaChecklistPage = React.lazy(() => import("./pages/MaturaChecklistPage"));
const MaturaChecklistTaskPage = React.lazy(() => import("./pages/MaturaChecklistTaskPage"));
const MaturaArchiveCatalogPage = React.lazy(() => import("./pages/MaturaArchiveCatalogPage"));
const MaturaArchiveDocumentPage = React.lazy(() => import("./pages/MaturaArchiveDocumentPage"));
const BasicMaturaArchivePage = React.lazy(() => import("./pages/BasicMaturaArchivePage"));
const RegionColoringPreviewPage = React.lazy(() => import("./pages/RegionColoringPreviewPage"));
const OfficialMaturaExercisesPage = React.lazy(() =>
  import("./pages/OfficialMaturaExercisesPage")
);
const MaturaExtendedPathPage = React.lazy(() => import("./pages/MaturaExtendedPathPage"));
const MaturaOriginalTaskPage = React.lazy(() =>
  import("./pages/MaturaOriginalTaskPage")
);
const MaturaExtendedOriginalTaskPage = React.lazy(() =>
  import("./pages/MaturaExtendedOriginalTaskPage")
);
const EighthGradeCoursePathPage = React.lazy(() => import("./pages/EighthGradeCoursePathPage"));
const EighthGradeStudentPanel = React.lazy(() =>
  import("./pages/EighthGradeStudentPanel")
);
const OfficialEighthGradeExercisesPage = React.lazy(() =>
  import("./pages/OfficialEighthGradeExercisesPage")
);
const EighthGradeExamPage = React.lazy(() =>
  import("./pages/EighthGradeExamPage")
);

// (opcjonalnie) prościutki spinner
function Loading() {
  return (
    <div style={{ padding: "24px 0", textAlign: "center", opacity: 0.7 }}>
      Ładowanie…
    </div>
  );
}

// Globalny banner: brak internetu
function OfflineBanner({ isOnline }) {
  if (isOnline) return null;

  const banner = (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: "fixed",
        left: 12,
        right: 12,
        bottom: "max(12px, env(safe-area-inset-bottom))",
        zIndex: 12000,
        width: "min(800px, calc(100vw - 24px))",
        margin: "0 auto",
        boxSizing: "border-box",
        padding: "11px 14px",
        borderRadius: 12,
        border: "1px solid rgba(255, 153, 0, 0.55)",
        background: "color-mix(in srgb, var(--color-bg) 88%, #ff9900 12%)",
        color: "var(--color-text)",
        fontSize: 14,
        lineHeight: 1.35,
        boxShadow: "0 10px 32px rgba(0, 0, 0, 0.38)",
        backdropFilter: "blur(12px)",
      }}
    >
      <strong>Brak internetu.</strong> Postępy i wyniki mogą się nie zapisać.
      Połącz się z internetem i dopiero wtedy klikaj <strong>„Zapisz wynik”</strong>{" "}
      / <strong>„Sprawdź i zapisz wynik”</strong>.
    </div>
  );

  return typeof document !== "undefined" ? createPortal(banner, document.body) : banner;
}

function AuditBanner() {
  const { audit, user, endAudit } = useContext(AuthContext);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState("");

  if (!audit?.active) return null;

  const end = async () => {
    if (ending) return;
    setEnding(true);
    setError("");
    const out = await endAudit();
    setEnding(false);
    if (!out?.ok) {
      setError(out?.error || "Nie udało się zakończyć audytu.");
      return;
    }
    window.location.assign("/admin/uzytkownicy");
  };

  return (
    <div
      role="status"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10000,
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 14px",
        background: "rgba(17, 24, 39, 0.96)",
        borderBottom: "1px solid rgba(255,255,255,0.18)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        flexWrap: "wrap",
        fontSize: 14,
      }}
    >
      <strong>Tryb audytu read-only</strong>
      <span style={{ opacity: 0.86 }}>
        Oglądasz konto: {user?.email || "uczeń"}
      </span>
      {audit?.adminEmail ? (
        <span style={{ opacity: 0.7 }}>Admin: {audit.adminEmail}</span>
      ) : null}
      <button
        type="button"
        className="btn-secondary"
        onClick={end}
        disabled={ending}
        style={{ minHeight: 30, padding: "4px 10px" }}
      >
        {ending ? "Wracam..." : "Zakończ audyt"}
      </button>
      {error ? <span style={{ color: "#fca5a5", fontWeight: 700 }}>{error}</span> : null}
    </div>
  );

}

// Helper do domyślnej strony roli (dla RequireGuest)
function roleHomePath(role) {
  if (role === "student") return "/";
  if (role === "parent") return "/rodzic";
  if (role === "tutor") return "/korepetytor";
  if (role === "admin") return "/admin";
  return "/konto";
}

function RedirectQuizPath() {
  const { combinedId } = useParams();
  if (!combinedId || !combinedId.includes("_")) {
    return (
      <div style={{ color: "red" }}>Nieprawidłowy format ścieżki quizu</div>
    );
  }
  const [sectionId, topicId] = combinedId.split("_");
  return <Navigate to={`/quiz/${sectionId}/${topicId}`} replace />;
}

// Tylko dla NIEzalogowanych (blokuje /login i /register)
function RequireGuest({ children }) {
  const { user, isAuthReady } = useContext(AuthContext);
  if (!isAuthReady) return null;
  if (user?.role) return <Navigate to={roleHomePath(user.role)} replace />;
  return children;
}

function RequireSubscriptionPayments({ children }) {
  const { user, isAuthReady, courseAccessMode, isCourseAccessModeReady } = useContext(AuthContext);
  if (!isAuthReady || !isCourseAccessModeReady) return null;
  if (courseAccessMode === "open") {
    return <Navigate to={user ? "/konto" : "/"} replace />;
  }
  return children;
}

function CampaignActivationGate({ children }) {
  const { user, isAuthReady, entitlements, isEntitlementsReady } = useContext(AuthContext);
  const location = useLocation();

  if (!isAuthReady) return null;
  if (user?.role === "student" && !isEntitlementsReady) return null;

  const path = String(location.pathname || "/");
  const isSeoLesson = seoTopicRegistry.some((topic) => `/${topic.slug}` === path);
  const isLearningPath =
    path === "/" ||
    path.startsWith("/demo/") ||
    path.startsWith("/demo-temat/") ||
    path.startsWith("/home") ||
    path.startsWith("/dzial/") ||
    path.startsWith("/lekcja/") ||
    path.startsWith("/quiz/") ||
    path.startsWith("/panel-maturzysty") ||
    path.startsWith("/matura/") ||
    isSeoLesson;

  if (user?.role === "student" && entitlements?.campaignActivationRequired === true && isLearningPath) {
    return <Navigate to="/aktywuj-dostep" state={{ from: location }} replace />;
  }

  return children;
}

function FifaEntryRoute() {
  const { user, isAuthReady } = useContext(AuthContext);
  if (!isAuthReady) return null;
  if (user?.role === "student" || user?.role === "admin") return <FifaPage />;
  return <FifaPage guestMode />;
}

function AppShell({ children }) {
  const location = useLocation();
  const isWhiteboardLobby = location.pathname === "/tablica";
  const isWhiteboardGroup = /^\/tablica\/grupa\/[^/]+$/.test(location.pathname || "");
  const isWhiteboardRoom = /^\/tablica\/[^/]+$/.test(location.pathname || "");
  const isWidePage =
    location.pathname === "/number-defense" ||
    location.pathname === "/panel-inwestora" ||
    location.pathname === "/grant-pracuj" ||
    location.pathname === "/tour" ||
    location.pathname === "/demo-tour" ||
    location.pathname === "/demo/tablica";
  const isAuthPage = location.pathname === "/login" || location.pathname === "/register";
  const isLearningPathPage = /^\/matura\/sciezka(?:\/|$)/.test(location.pathname || "");

  const shellMaxWidth = isWidePage || isWhiteboardRoom ? "1440px" : "800px";
  const shellHeaderPadding = "20px 0 0 0";
  const shellContentPadding = isWhiteboardLobby || isWhiteboardGroup ? "20px 20px 20px 20px" : "20px 20px";

  return (
    <div
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      <div
        style={{
          margin: "0 auto",
          padding: shellHeaderPadding,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <AuditBanner />
        <Header />
      </div>
        <main
          style={{
            flex: 1,
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            id="app-content-top"
            style={{
              maxWidth: shellMaxWidth,
              width: "100%",
              flex: isAuthPage || isLearningPathPage ? 1 : undefined,
              display: isAuthPage || isLearningPathPage ? "flex" : undefined,
              flexDirection: isAuthPage || isLearningPathPage ? "column" : undefined,
              margin: "0 auto",
              padding: shellContentPadding,
              boxSizing: "border-box",
            }}
          >
            {children}
          </div>
        </main>
      <Footer />
    </div>
  );
}

function App() {
  const { user, isAuthReady } = useContext(AuthContext);
  const location = useLocation();

  // ✅ GLOBAL: przewijaj na górę po każdej zmianie URL (pathname)
useEffect(() => {
  if (location.hash) return;
  const pathTopic = new URLSearchParams(location.search).get("topic");
  if (/\/matura\/sciezka\/?$/.test(location.pathname) && pathTopic) return;

  const el = document.getElementById("app-content-top");
  if (el) {
    el.scrollIntoView({ behavior: "auto", block: "start" });
  } else {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }
}, [location.pathname, location.search, location.hash]);

  // Pomiar SPA + konwersja "klik reklamowy -> realne wejście na widok docelowy".
  useEffect(() => {
    if (typeof window === "undefined") return;

    const pagePath = `${location.pathname || ""}${location.search || ""}`;
    trackMarketingPageView({
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title || undefined,
    });

    if (hasAdsClickParam(location.search)) {
      try {
        window.sessionStorage.setItem(ADS_CLICK_SESSION_KEY, "1");
      } catch {}
    }

    let hadAdsClick = false;
    let conversionAlreadySent = false;
    try {
      hadAdsClick = window.sessionStorage.getItem(ADS_CLICK_SESSION_KEY) === "1";
      conversionAlreadySent = window.sessionStorage.getItem(ADS_CONVERSION_SESSION_KEY) === "1";
    } catch {}

    if (!hadAdsClick || conversionAlreadySent) return;
    if (!isQualifiedViewPath(location.pathname)) return;

    trackMarketingEvent("landing_to_topic_view", {
      event_category: "engagement",
      event_label: location.pathname,
      value: 1,
    }, "ViewContent");
    trackGoogleConversion("qualifiedView", { value: 1, currency: "PLN" });

    try {
      window.sessionStorage.setItem(ADS_CONVERSION_SESSION_KEY, "1");
    } catch {}
  }, [location.pathname, location.search]);


  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  return (
    <AppShell>
      <OfflineBanner isOnline={isOnline} />

      <CookieConsentBanner />

      <ProgressSyncInit />
      <GlobalFeedbackButton />
      <GlobalNavigationFeedback />
      <SeoGuestLearningGate />

      {!isAuthReady ? (
        <Loading />
      ) : user && !user.role && location.pathname !== "/wybor-roli" ? (
        <Navigate to="/wybor-roli" replace />
      ) : (
        <CampaignActivationGate>
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/korepetycje" element={<TutoringProspectRoute><TutoringLandingPage /></TutoringProspectRoute>} />
            <Route path="/grant-pracuj" element={<GrantInvestorTourPage />} />
            <Route path="/tour" element={<GrantInvestorTourPage />} />
            <Route path="/demo-tour" element={<GrantInvestorTourPage />} />
            <Route path="/demo/tablica" element={<WhiteboardRoomPage demoMode />} />

            {/* ===================== */}
            {/* DEMO – PUBLIC PREVIEW */}
            {/* ===================== */}

            {/* Pełny arkusz publiczny w ramach wspólnego limitu demo. */}
            <Route path="/demo/arkusz-maturalny" element={<Navigate to="/demo/matura/sciezka?mode=diagnosis" replace />} />
            <Route path="/demo/matura-exam/:year/:month/:sheetLabel" element={<MaturaExamPage demoMode />} />
            <Route path="/demo/egzamin-osmoklasisty/sciezka" element={<RequireAnyRole roles={["admin"]}><EighthGradeCoursePathPage demoMode /></RequireAnyRole>} />
            <Route path="/demo/matura/sciezka" element={<MaturaCoursePathPage demoMode />} />
            <Route path="/demo/matura/zadania-oryginalne/:sheetId/:taskNo" element={<MaturaOriginalTaskPage demoMode />} />
            <Route path="/demo/matura/zadania-oryginalne-rozszerzenie/:sourceId" element={<MaturaExtendedOriginalTaskPage />} />
            <Route path="/demo/matura/sciezka/przygotowanie/:code" element={<MaturaCoursePreparationPage demoMode />} />
            <Route path="/demo/matura/sciezka/zadania/:code" element={<MaturaCourseTasksPage demoMode />} />
            <Route path="/demo/matura/sciezka/sprawdzian/:sectionNo" element={<MaturaCourseTasksPage demoMode />} />
            {seoTopicRegistry.map((topic) => (
              <Route
                key={topic.slug}
                path={`/${topic.slug}`}
                element={<SeoTopicLandingPage topic={topic} />}
              />
            ))}
            {legacyMaturaSeoRedirects.map((redirect) => (
              <Route
                key={`legacy-seo-${redirect.fromSlug}`}
                path={`/${redirect.fromSlug}`}
                element={<Navigate to={`/${redirect.toSlug}`} replace />}
              />
            ))}

            <Route path="/demo/home" element={<HomeLayout />} />
            <Route path="/demo/dzial/:sectionIndex" element={<SectionLayout />} />
            <Route path="/demo/lekcja/:sectionIndex/:subtopicIndex" element={<LessonEntryLayout />} />
            <Route
              path="/demo/lekcja/:sectionIndex/:subtopicIndex/:subIndex"
              element={<LessonLayout />}
            />
            <Route path="/demo/quiz/:sectionIndex/:subtopicIndex" element={<DynamicQuizLoader />} />

            <Route path="/demo-temat/home" element={<HomeLayout />} />
            <Route path="/demo-temat/dzial/:sectionIndex" element={<SectionLayout />} />
            <Route path="/demo-temat/lekcja/:sectionIndex/:subtopicIndex" element={<LessonEntryLayout />} />
            <Route
              path="/demo-temat/lekcja/:sectionIndex/:subtopicIndex/:subIndex"
              element={<LessonLayout />}
            />
            <Route path="/demo-temat/quiz/:sectionIndex/:subtopicIndex" element={<DynamicQuizLoader />} />

            {/* Tylko dla niezalogowanych */}
            <Route
              path="/login"
              element={
                <RedirectIfAuth to="/">
                  <LoginPage />
                </RedirectIfAuth>
              }
            />
            <Route
              path="/register"
              element={
                <RedirectIfAuth to="/">
                  <RegisterPage />
                </RedirectIfAuth>
              }
            />
            <Route
              path="/verify-email"
              element={
                <RedirectIfAuth to="/">
                  <VerifyEmailPage />
                </RedirectIfAuth>
              }
            />

            {/* ✅ NOWE: zapomniałem hasła */}
            <Route
              path="/forgot-password"
              element={
                <RedirectIfAuth to="/konto">
                  <ForgotPasswordPage />
                </RedirectIfAuth>
              }
            />

            {/* Public legal pages */}
            <Route path="/cennik" element={<Cennik />} />
            <Route path="/regulamin" element={<Regulamin />} />
            <Route
              path="/polityka-prywatnosci"
              element={<PolitykaPrywatnosci />}
            />
            <Route
              path="/platnosc"
              element={
                <RequireSubscriptionPayments>
                  <Platnosc />
                </RequireSubscriptionPayments>
              }
            />

            {/* Aliasy */}
            <Route
              path="/rejestracja"
              element={<Navigate to="/register" replace />}
            />
            <Route
              path="/logowanie"
              element={<Navigate to="/login" replace />}
            />
            <Route path="/wybor-roli" element={<RoleSelectionPage />} />
            <Route path="/debug" element={<DebugTools />} />

            {/* Kurs — student LUB admin, z kontrolą dostępu wg subskrypcji */}
            <Route
              path="/home"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <HomeLayout />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/szukaj"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <SearchLessonsPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/dzial/:sectionIndex"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <SectionLayout />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/lekcja/:sectionIndex/:subtopicIndex"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <LessonEntryLayout />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/lekcja/:sectionIndex/:subtopicIndex/:subIndex"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <LessonLayout />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/wykres"
              element={
                <RequireAnyRole roles={["student", "tutor", "admin"]}>
                  <RequireTutoringToolsAccess>
                    <GraphPage />
                  </RequireTutoringToolsAccess>
                </RequireAnyRole>
              }
            />

            <Route
              path="/poker"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <PokerClockPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />

            <Route path="/pokergame" element={<PokerLobbyPage />} />

            <Route path="/pokergame/:code" element={<PokerTablePage />} />

            <Route path="/fifa-zaproszenie/:leagueCode" element={<FifaInvitePage />} />

            <Route
              path="/fifa"
              element={<FifaEntryRoute />}
            />

            <Route
              path="/fifa/:leagueCode"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <FifaPage />
                </RequireAnyRole>
              }
            />

            <Route
              path="/number-defense"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <NumberDefensePage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />

            <Route
              path="/tablica"
              element={
                <RequireAnyRole roles={["student", "tutor", "admin"]}>
                  <RequireTutoringToolsAccess>
                    <WhiteboardLobbyPage />
                  </RequireTutoringToolsAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/aktywuj-dostep"
              element={
                <RequireAuth>
                  <CampaignActivationPage />
                </RequireAuth>
              }
            />

            <Route
              path="/tablica/grupa/:groupId"
              element={
                <RequireAnyRole roles={["student", "tutor", "admin"]}>
                  <RequireTutoringToolsAccess>
                    <WhiteboardLobbyPage groupPage />
                  </RequireTutoringToolsAccess>
                </RequireAnyRole>
              }
            />

            <Route
              path="/tablica/:code"
              element={
                <RequireAnyRole roles={["student", "tutor", "admin"]}>
                  <RequireTutoringToolsAccess>
                    <WhiteboardRoomPage />
                  </RequireTutoringToolsAccess>
                </RequireAnyRole>
              }
            />

            <Route
              path="/praca-domowa/:id"
              element={
                <RequireAnyRole roles={["student", "tutor", "admin"]}>
                  <HomeworkPage />
                </RequireAnyRole>
              }
            />
            <Route
              path="/praca-domowa/:id/:subIndex"
              element={
                <RequireAnyRole roles={["student", "tutor", "admin"]}>
                  <HomeworkLessonPage />
                </RequireAnyRole>
              }
            />

            <Route
              path="/matura-exam/:year/:month/:sheetLabel"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <MaturaExamPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />

            <Route path="/matura/zadania" element={<RequireAnyRole roles={["student", "admin"]}><RequireCourseAccess><MaturaChecklistPage /></RequireCourseAccess></RequireAnyRole>} />
            <Route path="/matura/zadania/:level/dzial/:sectionNo" element={<RequireAnyRole roles={["student", "admin"]}><RequireCourseAccess><MaturaChecklistPage /></RequireCourseAccess></RequireAnyRole>} />
            <Route path="/matura/arkusze" element={<RequireAnyRole roles={["student", "admin"]}><RequireCourseAccess><MaturaPapersPage /></RequireCourseAccess></RequireAnyRole>} />
            <Route path="/matura/checklista" element={<RequireAnyRole roles={["student", "admin"]}><RequireCourseAccess><MaturaChecklistPage /></RequireCourseAccess></RequireAnyRole>} />
            <Route path="/matura/checklista/:sheetId/:taskNo" element={<RequireAnyRole roles={["student", "admin"]}><RequireCourseAccess><MaturaChecklistTaskPage /></RequireCourseAccess></RequireAnyRole>} />
            <Route path="/matura/katalog" element={<RequireAnyRole roles={["admin"]}><MaturaArchiveCatalogPage /></RequireAnyRole>} />
            <Route path="/matura/katalog/dokument/:sheetId/:kind" element={<RequireAnyRole roles={["student", "tutor", "admin"]}><RequireCourseAccess><MaturaArchiveDocumentPage /></RequireCourseAccess></RequireAnyRole>} />
            {/* Panel maturzysty */}
            {process.env.NODE_ENV === "development" && <Route
              path="/matura/podglad-kolorowania"
              element={<RequireAnyRole roles={["admin"]}><RegionColoringPreviewPage /></RequireAnyRole>}
            />}
            {process.env.NODE_ENV === "development" && <Route
              path="/matura/archiwum-podstawa"
              element={<RequireAnyRole roles={["admin"]}><BasicMaturaArchivePage /></RequireAnyRole>}
            />}
            <Route
              path="/matura/rozszerzenie"
              element={<RequireAnyRole roles={["student", "tutor", "admin"]}><RequireTutoringToolsAccess title="Ścieżka rozszerzona w fazie testowej" description="Ścieżka do matury rozszerzonej jest obecnie dostępna dla uczniów przypisanych do korepetytora, na tych samych zasadach co tablica."><MaturaExtendedPathPage /></RequireTutoringToolsAccess></RequireAnyRole>}
            />
            <Route
              path="/panel-maturzysty"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <MaturaStudentPanel />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/matura/arkusze/:sheetId"
              element={
                <RequireAnyRole roles={["student", "tutor", "admin"]}>
                  <RequireCourseAccess>
                    <OfficialMaturaExercisesPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/matura/zadania-oryginalne/:sheetId/:taskNo"
              element={
                <RequireAnyRole roles={["student", "tutor", "admin"]}>
                  <RequireCourseAccess>
                    <MaturaOriginalTaskPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/matura/zadania-oryginalne-rozszerzenie/:sourceId"
              element={
                <RequireAnyRole roles={["student", "tutor", "admin"]}>
                  <RequireCourseAccess>
                    <MaturaExtendedOriginalTaskPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/matura/sciezka"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <MaturaCoursePathPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/matura/plan"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <MaturaPlannerPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/matura/sciezka/zadania/:code"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <MaturaCourseTasksPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/matura/sciezka/przygotowanie/:code"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <MaturaCoursePreparationPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/matura/sciezka/sprawdzian/:sectionNo"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <MaturaCourseTasksPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/egzamin-osmoklasisty/sciezka"
              element={<RequireAnyRole roles={["admin"]}><EighthGradeCoursePathPage /></RequireAnyRole>}
            />
            <Route
              path="/panel-osmoklasisty"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <EighthGradeStudentPanel />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/egzamin-osmoklasisty/arkusze/:sheetId"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <OfficialEighthGradeExercisesPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/egzamin-osmoklasisty-arkusz/:year/:month/:sheetLabel"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <EighthGradeExamPage />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />
            <Route
              path="/admin/panel-maturzysty/:email"
              element={
                <RequireAdmin>
                  <MaturaStudentPanel readOnly />
                </RequireAdmin>
              }
            />
            <Route
              path="/rodzic/panel-maturzysty/:email"
              element={
                <RequireRole roles={["parent"]}>
                  <MaturaStudentPanel readOnly />
                </RequireRole>
              }
            />
            <Route
              path="/korepetytor/panel-maturzysty/:email"
              element={
                <RequireRole roles={["tutor"]}>
                  <MaturaStudentPanel readOnly />
                </RequireRole>
              }
            />
            <Route
              path="/admin/sciezka-maturalna/:email"
              element={
                <RequireAdmin>
                  <MaturaCoursePathPage readOnly />
                </RequireAdmin>
              }
            />
            <Route
              path="/rodzic/sciezka-maturalna/:email"
              element={
                <RequireRole roles={["parent"]}>
                  <MaturaCoursePathPage readOnly />
                </RequireRole>
              }
            />
            <Route
              path="/korepetytor/sciezka-maturalna/:email"
              element={
                <RequireRole roles={["tutor"]}>
                  <MaturaCoursePathPage readOnly />
                </RequireRole>
              }
            />

            {/* Panele ról */}
            <Route
              path="/panel-inwestora"
              element={
                <RequireInvestmentAccess>
                  <InvestmentDashboard />
                </RequireInvestmentAccess>
              }
            />
            <Route
              path="/rodzic"
              element={
                <RequireRole roles={["parent"]}>
                  <ParentDashboard />
                </RequireRole>
              }
            />
            <Route
              path="/korepetytor"
              element={
                <RequireRole roles={["tutor"]}>
                  <TutorDashboard />
                </RequireRole>
              }
            />
            <Route
              path="/uczen"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <StudentDashboard />
                </RequireAnyRole>
              }
            />
            <Route
              path="/uczen/korepetycje"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <StudentTutoringDashboard />
                </RequireAnyRole>
              }
            />
            <Route
              path="/rodzic/korepetycje/:email"
              element={
                <RequireRole roles={["parent"]}>
                  <ParentChildTutoringDashboard />
                </RequireRole>
              }
            />
            <Route
              path="/rodzic/korepetycje/:email/praca-domowa/:id"
              element={
                <RequireRole roles={["parent"]}>
                  <HomeworkPage />
                </RequireRole>
              }
            />

            {/* Admin */}
            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminDashboard />
                </RequireAdmin>
              }
            />
            <Route
              path="/admin/sciezka-spis"
              element={
                <RequireAdmin>
                  <TextbookOutlinePage />
                </RequireAdmin>
              }
            />
            <Route
              path="/admin/*"
              element={
                <RequireAdmin>
                  <AdminDashboard />
                </RequireAdmin>
              }
            />

            {/* Konto użytkownika */}
            <Route
              path="/konto"
              element={
                <RequireAuth>
                  <UserAccount />
                </RequireAuth>
              }
            />
            <Route
              path="/konto/subskrypcja"
              element={
                <RequireAuth>
                  <UserAccount initialTab="subscription" />
                </RequireAuth>
              }
            />
            <Route
              path="/moj-postep"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <StudentDashboard progressOnly />
                </RequireAnyRole>
              }
            />
            <Route
              path="/konto/haslo"
              element={
                <RequireAuth>
                  <UserAccount initialTab="password" />
                </RequireAuth>
              }
            />
            <Route
              path="/konto/profil"
              element={
                <RequireAuth>
                  <UserAccount initialTab="profile" />
                </RequireAuth>
              }
            />

            <Route
              path="/konto/zgloszenia"
              element={
                <RequireAuth>
                  <UserAccount initialTab="feedback" />
                </RequireAuth>
              }
            />

            {/* Quiz */}
            <Route
              path="/quiz/:sectionIndex/:subtopicIndex"
              element={
                <RequireAnyRole roles={["student", "admin"]}>
                  <RequireCourseAccess>
                    <DynamicQuizLoader />
                  </RequireCourseAccess>
                </RequireAnyRole>
              }
            />

            {/* Widoki postępów */}
            <Route
              path="/admin/postepy/:email"
              element={
                <RequireAdmin>
                  <StudentProgressView />
                </RequireAdmin>
              }
            />
            <Route
              path="/rodzic/postepy/:email"
              element={
                <RequireRole roles={["parent"]}>
                  <StudentProgressView />
                </RequireRole>
              }
            />
            <Route
              path="/korepetytor/postepy/:email"
              element={
                <RequireRole roles={["tutor"]}>
                  <StudentProgressView />
                </RequireRole>
              }
            />

            {/* Płatności */}
            <Route path="/platnosc/ok" element={<PaymentOkPage />} />
            <Route path="/payments/ok" element={<PaymentOkPage />} />
            <Route
              path="/platnosc/niepowodzenie"
              element={<PaymentFailPage />}
            />
            <Route path="/payments/fail" element={<PaymentFailPage />} />

            {/* Snapshoty */}
            <Route path="/snapshot/:id" element={<QuizSnapshotView />} />
            <Route
              path="/matura-snapshot/:id"
              element={<MaturaExamSnapshotView />}
            />
            <Route path="/quiz/:combinedId" element={<RedirectQuizPath />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </CampaignActivationGate>
      )}
    </AppShell>
  );
}

export default App;

